/**
 * ✅ ADİSYON FİŞİ YAZDIRMA SERVİSİ
 * Restoran modunda müşteriye verilen adisyon fişlerini yazdırmak için kullanılır.
 * PrintReceiptService.tsx ile aynı ayarları (logo dahil) kullanır.
 */

import { printReceipt as sendPrintReceipt } from '../../../../services/localPrintService';

// Yerel yazıcı servisi endpoint (varsayılan, parametre ile override edilebilir)
const DEFAULT_LOCAL_SERVICE_URL = 'http://localhost:3003';

// Adisyon fiş verisi için tip tanımlaması
export interface AdisyonReceiptItem {
  name: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  vatRate?: number;
}

export interface AdisyonReceiptData {
  invoiceNumber: string;
  date: string;
  time: string;
  tableName: string;
  items: AdisyonReceiptItem[];
  subtotal: number;
  vatTotal: number;
  grandTotal: number;
  waiterName?: string;
  orderNumber?: string;
  companyInfo?: {
    name: string;
    address: string;
    taxNumber: string;
  };
  thankYouMessage?: string;
  standardFooterNote?: string;
  // ✅ YENİ: Logo başlık ayarları (PrintReceiptService ile aynı)
  headerType?: 'text' | 'logo';
  logoUrl?: string;
  // İndirim bilgileri
  showDiscountInfo?: boolean;
  discountAmount?: number;
  discountType?: 'percentage' | 'amount' | null;
  discountPercentage?: number;
}

/**
 * Adisyon fişi HTML şablonunu oluşturur
 * PrintReceiptService.tsx ile aynı stil ve ölçüler kullanılır (logo desteği dahil)
 */
export const createAdisyonReceiptHtml = (data: AdisyonReceiptData): string => {
  const items = data.items || [];
  
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>Adisyon Fişi</title>
      <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap" rel="stylesheet">
      <style>
        body {
          font-family: 'Poppins', sans-serif;
          font-size: 9pt;
          width: 80mm;
          margin: 0;
          padding: 2mm 4mm 3mm 4mm;
          box-sizing: border-box;
          line-height: 1.1;
        }
        .header {
          text-align: center;
          margin-bottom: 3px;
          padding-top: 0;
        }
        .header h2 {
          margin: 0;
          margin-bottom: 2px;
        }
        .header img {
          max-width: 50mm;
          max-height: 18mm;
          margin-left: -3mm; /* Logo sola kaydırma - PrintReceiptService ile aynı */
          image-rendering: -webkit-optimize-contrast;
        }
        .company-info {
          text-align: center;
          margin-bottom: 3px;
          font-size: 8pt;
        }
        .company-info p {
          margin: 0 0 1px 0;
        }
        .table-name {
          font-size: 18pt;
          font-weight: 900;
          margin: 4px 0;
          padding: 4px 0;
          color: #000;
          text-align: center;
          border-top: 2px solid #000;
          border-bottom: 2px solid #000;
        }
        .waiter-name {
          font-size: 10pt;
          color: #000;
          font-weight: bold;
          margin: 2px 0;
          text-align: center;
        }
        .order-number {
          font-size: 14pt;
          text-align: center;
          margin-bottom: 8px;
          margin-top: 5px;
          font-weight: bold;
          letter-spacing: 2px;
          color: #000;
          display: none;
        }
        .grand-total {
          font-size: 18pt;
          font-weight: bold;
        }
        .invoice-details {
          margin-bottom: 3px;
          font-size: 8pt;
        }
        .invoice-details p {
          margin: 0 0 1px 0;
        }
        .items-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 8pt;
          margin-top: 2px;
          table-layout: fixed;
        }
        .items-table th, .items-table td {
          text-align: left;
          padding: 1px 0;
        }
        .items-table th {
          border-bottom: 1px solid #ddd;
        }
        .summary {
          margin-top: 3px;
          text-align: right;
          font-size: 8pt;
        }
        .summary p {
          margin: 0 0 1px 0;
        }
        .footer {
          margin-top: 5px;
          text-align: center;
          font-size: 8pt;
          border-top: 1px dashed #ddd;
          padding-top: 3px;
        }
        .footer p {
          margin: 0 0 1px 0;
        }
        .bold {
          font-weight: bold;
        }
        .center {
          text-align: center;
        }
        .right {
          text-align: right;
        }
        .adisyon-label {
          font-size: 10pt;
          text-align: center;
          margin: 8px 0;
          font-weight: bold;
          border: 1px solid #ddd;
          padding: 3px;
          border-radius: 4px;
          background-color: #f9f9f9;
          letter-spacing: 2px;
        }
      </style>
    </head>
    <body>
      <div class="header">
        ${data.headerType === 'logo' && data.logoUrl ? 
          `<img src="${data.logoUrl}" alt="Logo" />` : 
          `<h2 style="margin: 0">${data.companyInfo?.name || 'AQUASOFT'}</h2>`
        }
      </div>
      
      ${data.headerType === 'logo' && data.logoUrl ? '' : `
      <div class="company-info">
        ${data.companyInfo?.address ? `<p>${data.companyInfo.address}</p>` : ''}
        ${data.companyInfo?.taxNumber ? `<p>VKN: ${data.companyInfo.taxNumber}</p>` : ''}
      </div>
      `}
      
      <div class="table-name">${data.tableName || 'Masa'}</div>
      
      ${data.orderNumber ? `<div class="order-number">SİPARİŞ : ${data.orderNumber}</div>` : ''}
      ${data.waiterName ? `<div class="waiter-name">Garson: ${data.waiterName}</div>` : ''}
      
      <div class="invoice-details">
        <p><span class="bold">Tarih:</span> ${data.date} ${data.time}</p>
      </div>
      
      <table class="items-table">
        <thead>
          <tr>
            <th style="width: 45%;">Ürün</th>
            <th class="right" style="width: 12%;">Adet</th>
            <th class="right" style="width: 20%;">Fiyat</th>
            <th class="right" style="width: 23%;">Tutar</th>
          </tr>
        </thead>
        <tbody>
          ${items.map(item => `
            <tr>
              <td>${item.name}</td>
              <td class="right">${item.quantity}</td>
              <td class="right">${item.unitPrice.toFixed(2)}</td>
              <td class="right">${item.totalPrice.toFixed(2)}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
      
      <div class="summary">
        ${data.showDiscountInfo && data.discountAmount && data.discountAmount > 0 ? `
        <p>Ara Toplam: ${(data.grandTotal + data.discountAmount).toFixed(2)} TL</p>
        <p style="color: #c00;">İndirim${data.discountType === 'percentage' && data.discountPercentage ? ` (%${data.discountPercentage})` : ''}: -${data.discountAmount.toFixed(2)} TL</p>
        ` : ''}
        <p class="grand-total">Genel Toplam: ${data.grandTotal.toFixed(2)} TL</p>
      </div>
      
      <div class="adisyon-label">ADİSYON FİŞİ</div>
      
      <div class="footer">
        ${data.standardFooterNote ? `<p>${data.standardFooterNote}</p>` : ''}
        ${data.thankYouMessage ? `<p>${data.thankYouMessage}</p>` : ''}
      </div>
    </body>
    </html>
  `;
};

/**
 * Adisyon fişini yazdırır
 */
export const printAdisyonReceipt = async (
  data: AdisyonReceiptData,
  printer: { id: string; name: string },
  localServiceUrl?: string
): Promise<void> => {
  const baseUrl = localServiceUrl || DEFAULT_LOCAL_SERVICE_URL;
  const htmlTemplate = createAdisyonReceiptHtml(data);
  
  const hasDiscountInHtml = htmlTemplate.includes('Ara Toplam') && htmlTemplate.includes('İndirim');
  console.log('🧾 [ADİSYON YAZDIR] Fiş yazdırılıyor:', {
    printer: printer.name,
    tableName: data.tableName,
    itemCount: data.items.length,
    total: data.grandTotal,
    serviceUrl: baseUrl,
    showDiscountInfo: data.showDiscountInfo,
    discountAmount: data.discountAmount,
    hasDiscountInHtml,
  });
  if (hasDiscountInHtml) {
    const summaryMatch = htmlTemplate.match(/<div class="summary">[\s\S]*?<\/div>/);
    console.log('🏷️ [ADİSYON HTML] Summary bölümü:', summaryMatch?.[0]);
  }
  
  await sendPrintReceipt({
    printerId: printer.id,
    printerName: printer.name,
    receiptData: {
      ...data,
      isAdisyonReceipt: true,
      htmlTemplate: htmlTemplate
    }
  }, { baseUrl });
  
  console.log('✅ [ADİSYON YAZDIR] Fiş yazıcıya gönderildi');
};

export default {
  createAdisyonReceiptHtml,
  printAdisyonReceipt
};
